import { useEffect, useState } from "react";

const STORAGE_KEY = "maxwell.surface";
const EVENT_NAME = "app:maxwell-surface";

/** Default rail width — wider than the legacy 360px fixed rail (spec §1.A). */
export const DEFAULT_W = 420;
/** Narrowest the rail can be dragged. */
export const MIN_W = 360;
/** Widest the rail can be dragged before the user should pop out instead. */
export const MAX_W = 720;

/** Persisted rail state: whether the rail is open and how wide it is. */
interface SurfaceState {
  open: boolean;
  width: number;
}

const DEFAULT_STATE: SurfaceState = { open: true, width: DEFAULT_W };

export interface MaxwellSurface extends SurfaceState {
  setOpen: (open: boolean) => void;
  setWidth: (width: number) => void;
  MIN_W: number;
  MAX_W: number;
}

/** Clamp a candidate width into the draggable range. */
export function clampWidth(width: number): number {
  return Math.min(MAX_W, Math.max(MIN_W, Math.round(width)));
}

/**
 * Rail surface preference (open/closed + width), persisted to `localStorage`
 * so the choice carries across page loads. Same-tab updates fan out via a
 * custom event so every consumer (rail, user-menu toggle) re-renders together;
 * cross-tab updates ride the native `storage` event. Mirrors the
 * `useGuidanceVisibility` pattern.
 *
 * Width is always read back clamped to `[MIN_W, MAX_W]`, so a stale or
 * hand-edited value can never paint the rail off-range.
 */
export function useMaxwellSurface(): MaxwellSurface {
  const [state, setState] = useState<SurfaceState>(() => readInitial());

  useEffect(() => {
    const onCustom = (e: Event) => {
      setState((e as CustomEvent<SurfaceState>).detail);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setState(readInitial());
    };
    window.addEventListener(EVENT_NAME, onCustom as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const commit = (next: SurfaceState) => {
    setState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage may throw in private mode / disabled storage — the
      // in-memory state still works for the current page.
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
  };

  return {
    ...state,
    setOpen: (open) => commit({ ...state, open }),
    setWidth: (width) => commit({ ...state, width: clampWidth(width) }),
    MIN_W,
    MAX_W,
  };
}

function readInitial(): SurfaceState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<SurfaceState>;
    return {
      open: typeof parsed.open === "boolean" ? parsed.open : DEFAULT_STATE.open,
      width: clampWidth(
        typeof parsed.width === "number" ? parsed.width : DEFAULT_W,
      ),
    };
  } catch {
    return DEFAULT_STATE;
  }
}
